import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FilterDrawer, FilterDrawerProps } from '@frontend/app/containers/ProductFulfillmentDashboard/FilterDrawer';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Label } from '@frontend/shadcn/components/ui/label';
import { CheckIcon, XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { MessageTemplatesQuery_templates } from '@frontend/app/queries/types/MessageTemplatesQuery';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { cloneDeep, isEqual } from 'lodash';
import { useMessagingContext } from '@frontend/hooks';
import {
  useUpdateMessageTemplateMutation,
  useDeleteMessageTemplateMutation,
  useCreateMessageTemplateMutation,
  useUpdateAdvanceTermsTemplateMutation,
  useUpdateBasicTermsTemplateMutation,
  useGetContentGuidelineTemplatesForProject,
  useGetTermsSettingsForProgram,
} from '@frontend/app/hooks';
import {
  EmailEditor,
  IEmailEditorState,
} from '@frontend/app/components/MessageComposer/EmailEditor';
import { useEventContext } from '@frontend/app/context/EventContext';
import { EventName, logger } from '@common';
import { P } from '@frontend/shadcn/components/typography/p';
import { CloneIcon, PenIcon, TrashIcon } from '@revfluence/fresh-icons/solid/esm';
import { useAuth } from '@frontend/context/authContext';
import { TermsType } from '@frontend/app/types/globalTypes';
import omitDeep from 'omit-deep-lodash';
import { useGetDefaultMemberFieldSchemasQuery } from '@frontend/app/hooks/useGetDefaultMemberFieldSchemasQuery';
import { Switch } from '@revfluence/fresh';
import { getDefaultTermsEmailTemplate } from './getDefaultTermsEmailTemplate';
import { TProject } from '../../types';
// import { TContentGuideline } from '../../TermsPage/components/types/state';

const { confirm } = Modal;

interface BriefEmailTemplateDrawerProps extends Omit<FilterDrawerProps, 'children'> {
  template: MessageTemplatesQuery_templates | null;
  projectId: number;
  program: TProject;
  onSaveSuccess: (template: MessageTemplatesQuery_templates) => void;
  onCancel: () => void;
  onDeleteSuccess: () => void;
  termsType: TermsType;
}

export const BriefEmailTemplateDrawer: React.FC<BriefEmailTemplateDrawerProps> = ({
  open,
  onOpenChange,
  template,
  projectId,
  program,
  onSaveSuccess,
  onCancel,
  onDeleteSuccess,
  termsType,
}) => {
  const [tempTitle, setTempTitle] = useState('');
  const [tempSubject, setTempSubject] = useState('');
  const [tempIsDefault, setTempIsDefault] = useState(false);
  const [editorState, setEditorState] = useState<IEmailEditorState | null>(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isCreating = useMemo(() => !template, [template]);

  const { showMessage, showError } = useMessagingContext();
  const [updateTemplate] = useUpdateMessageTemplateMutation();
  const [deleteTemplate] = useDeleteMessageTemplateMutation();
  const [createTemplate] = useCreateMessageTemplateMutation();
  const addEvent = useEventContext();

  const { clientInfo } = useAuth();

  const {
    data: {
      termsTemplate
    } = {},
    refetch: refetchTermsTemplate,
  } = useGetTermsSettingsForProgram({
    variables: {
      programId: projectId,
      termsType,
    },
    skip: !projectId,
  });

  const isDefault = (termsTemplate && !termsTemplate.emailTemplate?.id) || termsTemplate?.emailTemplate?.id === template?.id;

  const { data: guidelinesData } = useGetContentGuidelineTemplatesForProject({
    skip: !projectId,
    variables: {
      programId: projectId,
    },
    fetchPolicy: 'no-cache',
  });

  // const contentGuideline = useMemo(() => {
  //   if (!guidelinesData?.guidelines || !termsTemplate) return null;
  //   const {
  //     contentGuidelines,
  //     defaultContentGuidelines,
  //     memberContentGuidelines,
  //     termsContentGuidelines,
  //   } = guidelinesData.guidelines;
  //   const allContentGuidelines = [...contentGuidelines, ...defaultContentGuidelines, ...memberContentGuidelines, ...termsContentGuidelines];
  //   const contentGuideline: TContentGuideline = allContentGuidelines.find((guideline) => guideline.id === termsTemplate.contentGuidelineId);
  //   return contentGuideline;
  // }, [guidelinesData, termsTemplate]);

  const [updateAdvanceTermsTemplate] = useUpdateAdvanceTermsTemplateMutation();

  const [updateBasicTermsTemplate] = useUpdateBasicTermsTemplateMutation();

  const { defaultMemberFieldSchemas } = useGetDefaultMemberFieldSchemasQuery();

  const defaultNewTemplateState = useMemo(() => {
    if (!defaultMemberFieldSchemas) {
      return {
        title: '',
        subject: '',
        text: '',
        isDefault: false,
      };
    }
    const firstNameSchema = defaultMemberFieldSchemas.find((schema) => schema.name === 'First Name');
    if (!firstNameSchema) {
      return {
        title: '',
        subject: '',
        text: '',
        isDefault: false,
      };
    }
    const defaultEmail = getDefaultTermsEmailTemplate(program, clientInfo, firstNameSchema.id);
    return {
      title: defaultEmail.title,
      subject: defaultEmail.subject,
      text: defaultEmail.text,
      isDefault: false,
    };
  }, [clientInfo, defaultMemberFieldSchemas, program]);

  useEffect(() => {
    if (open) {
      setTempIsDefault(isDefault);
      if (template) {
        setTempTitle(template.title ?? '');
        setTempSubject(template.subject ?? '');
        setEditorState(null);
      } else {
        setTempTitle(defaultNewTemplateState.title);
        setTempSubject(defaultNewTemplateState.subject);
        setEditorState(null);
        setIsEditingTitle(true);
      }
      setHasChanged(false);
    }
  }, [template, open, defaultNewTemplateState, isDefault]);

  const handleEditorChange = useCallback((state: IEmailEditorState) => {
    const initialText = template?.text ?? defaultNewTemplateState.text;
    const initialRawState = initialText ? JSON.parse(initialText) : null;

    if (editorState && !isEqual(editorState.html, state.html)) {
      setHasChanged(true);
    } else if (!editorState && initialRawState && !isEqual(initialRawState, state.raw)) {
      setHasChanged(true);
    } else if (!editorState && !initialRawState && state.html !== '<p><br></p>') {
      setHasChanged(true);
    }
    setEditorState(state);
  }, [editorState, template, defaultNewTemplateState]);

  const handleSwitchChange = (checked: boolean) => {
    setTempIsDefault(checked);
    const originalIsDefault = isDefault ?? defaultNewTemplateState.isDefault;
    if (originalIsDefault !== checked) {
      setHasChanged(true);
    }
  };

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>, originalValue: string | null | undefined) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setter(newValue);
    if ((originalValue ?? (isCreating ? defaultNewTemplateState[e.target.id === 'email-subject' ? 'subject' : 'title'] : '')) !== newValue) {
      setHasChanged(true);
    }
  };

  const handleMakeDefault = async (
    emailTemplateId: number,
    subject: string,
    text: string,
  ) => {
    const {
      data: {
        termsTemplate
      } = {},
    } = await refetchTermsTemplate();

    let contentGuideline = null;
    const {
      contentGuidelines,
      defaultContentGuidelines,
      memberContentGuidelines,
      termsContentGuidelines,
    } = guidelinesData.guidelines;
    const allContentGuidelines = [...contentGuidelines, ...defaultContentGuidelines, ...memberContentGuidelines, ...termsContentGuidelines];
    contentGuideline = allContentGuidelines.find((guideline) => guideline.id === termsTemplate.contentGuidelineId);

    if (termsTemplate.termsType == TermsType.ADVANCED) {
      const termsTemplateData = {
        ...cloneDeep(termsTemplate),
        id: undefined,
        emailTemplate: undefined,
        contentRequirements: undefined,
        contentGuidelineId: undefined,
        introMessage: undefined,
        emailTemplateId,
        compensationPaymentValue: termsTemplate.compensationPaymentValue ?? 0,
      };
      await updateAdvanceTermsTemplate({
        variables: {
          id: termsTemplate.id,
          data: omitDeep({
            ...termsTemplateData,
            messageTemplateInput: {
              subject,
              text,
            },
            programId: projectId,
          }, '__typename'),
        },
      });
    } else {
      const termsTemplateData = {
        ...cloneDeep(termsTemplate),
        id: undefined,
        emailTemplate: undefined,
        contentGuidelineId: undefined,
        emailTemplateId,
        compensationPaymentValue: termsTemplate.compensationPaymentValue ?? 0,
      };
      await updateBasicTermsTemplate({
        variables: {
          id: termsTemplate.id,
          data: omitDeep({
            ...termsTemplateData,
            messageTemplateInput: {
              subject,
              text,
            },
            programId: projectId,
            contentGuideline,
          }, '__typename'),
        },
      });
    }
  };

  const handleSave = async () => {
    const currentRawContent = editorState?.raw;
    const currentText = currentRawContent ? JSON.stringify(currentRawContent) : (template?.text ?? defaultNewTemplateState.text);

    if (!currentRawContent && currentText === defaultNewTemplateState.text) {
       showError(new Error('Cannot save empty template body'));
       return;
    }
    if (!tempTitle) {
      showError(new Error('Template title cannot be empty'));
      return;
    }

    const saveData = {
      title: tempTitle,
      subject: tempSubject,
      text: currentText,
      type: 'terms',
      programId: template?.programId ?? projectId,
    };

    try {
      setIsSaving(true);
      if (isCreating) {
        const { data: { template: createdTemplate } } = await createTemplate({
          variables: {
            data: saveData,
          },
        });
        if (tempIsDefault) {
          await handleMakeDefault(createdTemplate.id, tempSubject, currentText);
        }
        showMessage({ type: 'success', content: 'Template has been created' });
        addEvent(EventName.MessageTemplates, {
          template_id: createdTemplate.id,
          template_name: tempTitle,
          action: 'create',
        });
        setHasChanged(false);
        setIsEditingTitle(false);
        onSaveSuccess(createdTemplate);
      } else {
        if (!template) return;
        const { data: { template: updatedTemplate } } = await updateTemplate({
          variables: {
            id: template.id,
            data: {
              title: tempTitle,
              subject: tempSubject,
              text: currentText,
              isDefault: tempIsDefault,
            },
          },
        });
        if (tempIsDefault && termsTemplate?.emailTemplate?.id !== template.id) {
          await handleMakeDefault(template.id, tempSubject, currentText);
        }
        showMessage({ type: 'success', content: 'Template has been saved' });
        addEvent(EventName.MessageTemplates, {
          template_id: template.id,
          template_name: tempTitle,
          action: 'save',
        });
        setHasChanged(false);
        setIsEditingTitle(false);
        onSaveSuccess(updatedTemplate);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async () => {
     if (!template) return;

     const duplicateData = {
       title: `${template.title ?? 'Template'} (Copy)`,
       subject: template.subject ?? '',
       text: template.text ?? defaultNewTemplateState.text,
       type: 'terms',
       programId: template.programId ?? projectId,
     };

     try {
       const { data: { template: duplicatedTemplate } } = await createTemplate({
         variables: { data: duplicateData },
       });
       showMessage({ type: 'success', content: 'Template duplicated successfully' });
       onSaveSuccess(duplicatedTemplate);
     } catch (error) {
       showError(error);
     }
  };

  const handleDelete = () => {
    if (isCreating || !template) return;
    confirm({
      title: 'Are you sure you want to delete this template?',
      icon: <ExclamationCircleOutlined />,
      content: "You can't undo this action",
      okText: 'Delete',
      okButtonProps: { danger: true },
      centered: true,
      zIndex: 99999,
      onOk() {
        deleteTemplate({
          variables: { id: template.id },
        }).then(({ data: { success } }) => {
          if (success) {
            addEvent(EventName.MessageTemplates, {
              template_id: template.id,
              template_name: template.title,
              action: 'delete',
            });
            showMessage({ type: 'success', content: 'Template has been deleted' });
            setHasChanged(false);
            onDeleteSuccess();
          }
        })
        .catch(logger.error);
      },
    });
  };

  const headerProps = {
     title: isCreating ? 'Create Brief Email Template' : 'Edit Brief Email Template',
  };

  const footerButtons = [
    { name: isCreating ? 'Create' : 'Save', onClick: handleSave, variant: 'primary' as const, disabled: !hasChanged, loading: isSaving },
    { name: 'Cancel', onClick: onCancel, variant: 'outline' as const },
  ];

  const additionalVariables = useMemo(() => {
    return { LINK_TO_TERMS: { label: 'Link To Brief' } };
  }, []);

  const initialEditorValue = template?.text ?? defaultNewTemplateState.text;

  return (
    <FilterDrawer
      open={open}
      onOpenChange={onOpenChange}
      headerProps={headerProps}
      footerButtons={footerButtons}
      footerButtonAlignment="left"
      width="576px"
    >
      <div className="p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-grow mr-4">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  value={tempTitle}
                  onChange={handleInputChange(setTempTitle, template?.title)}
                  className="text-base font-medium flex-grow"
                  placeholder="Enter Template Title"
                />
                <Button variant="ghost" size="icon" className="h-8 w-8" tooltip="Save Title" onClick={() => setIsEditingTitle(false)}>
                  <CheckIcon />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  tooltip="Cancel Title Edit"
                  onClick={() => {
                      setTempTitle(template?.title ?? defaultNewTemplateState.title);
                      setIsEditingTitle(false);
                    }}
                >
                  <XmarkIcon />
                </Button>
              </div>
              ) : (
                <div className="flex items-center gap-2">
                  <P className="text-base font-medium flex-grow border-0 p-0 h-auto focus-visible:ring-0" fontWeight="font-medium">{tempTitle}</P>
                  <Button variant="ghost" size="icon" className="h-8 w-8" tooltip="Edit Title" onClick={() => setIsEditingTitle(true)}>
                    <PenIcon />
                  </Button>
                </div>
              )}
            {!isCreating && (
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="ghost" size="icon" className="h-8 w-8" tooltip="Duplicate Template" onClick={handleDuplicate}>
                  <CloneIcon />
                </Button>
                <Button variant="ghost" size="icon" className="text-destructive h-8 w-8" tooltip="Delete Template" onClick={handleDelete}>
                  <TrashIcon />
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Switch
              id="default-switch"
              checked={tempIsDefault}
              onChange={handleSwitchChange}
              size="small"
            />
            <Label htmlFor="default-switch" className="text-sm font-normal">Default</Label>
          </div>
        </div>

        <div className="border border-border rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2">
            <Label htmlFor="email-subject" className="text-sm font-normal">Subject:</Label>
            <Input
              id="email-subject"
              className="flex-1 h-8 rounded-lg border-input"
              placeholder="Enter email subject"
              value={tempSubject}
              onChange={handleInputChange(setTempSubject, template?.subject)}
            />
          </div>
          <EmailEditor
            key={template?.id ?? 'new-template'}
            initialValue={initialEditorValue}
            onChange={handleEditorChange}
            enableVariableReplacement
            additionalVariables={additionalVariables}
            showProgramVariables={false}
          />
        </div>
      </div>
    </FilterDrawer>
  );
};
